import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
} from '@mui/material';
import {
  TrendingUp,
  Campaign,
  Groups,
  PersonAdd,
  Star,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabase';
import { emailService } from '../../services/email';
import { useTranslation } from 'react-i18next';
import CustomerSegments from './crm/components/CustomerSegments';
import CustomersTab from './crm/components/CustomersTab';
import CampaignsTab from './crm/components/CampaignsTab';
import CampaignDialog from './crm/components/CampaignDialog';

interface Customer {
  id: string;
  email: string;
  profile: any;
  orders: any[];
  totalSpent: number;
  lastOrderDate?: string;
  tags: string[];
  notes?: string;
  isVip: boolean;
  created_at: string;
}

interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  content: string;
  recipients: string[];
  status: 'draft' | 'scheduled' | 'sent';
  sent_at?: string;
  open_rate?: number;
  click_rate?: number;
}

const CRM: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    subject: '',
    content: '',
    recipients: [] as string[],
  });

  useEffect(() => {
    fetchCustomers();
    fetchCampaigns();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      // Fetch all orders with buyer information
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select(`
          *,
          buyer_profile:user_profiles!buyer_id (*),
          order_items (*)
        `)
        .eq('seller_id', user?.id);

      if (ordersError) throw ordersError;

      // Group by customer
      const customerMap = new Map<string, Customer>();

      orders?.forEach(order => {
        const customerId = order.buyer_id;
        const existing: Customer = customerMap.get(customerId) || {
          id: customerId,
          email: order.buyer_profile?.email || '',
          profile: order.buyer_profile,
          orders: [] as any[],
          totalSpent: 0,
          tags: [] as string[],
          isVip: false,
          created_at: order.created_at,
          lastOrderDate: undefined,
          notes: undefined,
        };

        existing.orders.push(order);
        existing.totalSpent += order.total_amount;
        existing.lastOrderDate = order.created_at;

        // Auto-tag based on spending
        if (existing.totalSpent > 10000 && !existing.tags.includes('VIP')) {
          existing.tags.push('VIP');
          existing.isVip = true;
        }
        if (existing.orders.length > 5 && !existing.tags.includes('Frequent Buyer')) {
          existing.tags.push('Frequent Buyer');
        }

        customerMap.set(customerId, existing);
      });

      setCustomers(Array.from(customerMap.values()));
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      // Mock campaigns data
      const mockCampaigns: EmailCampaign[] = [
        {
          id: '1',
          name: 'Spring Sale Campaign',
          subject: '30% Off Medical Equipment - Spring Sale',
          content: 'Check out our latest deals on refurbished medical equipment...',
          recipients: ['customer1@example.com', 'customer2@example.com'],
          status: 'sent',
          sent_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          open_rate: 45.5,
          click_rate: 12.3,
        },
        {
          id: '2',
          name: 'New Product Launch',
          subject: 'Introducing Our Latest Medical Equipment',
          content: 'We are excited to announce our new line of products...',
          recipients: [],
          status: 'draft',
        },
      ];
      setCampaigns(mockCampaigns);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleCustomerVip = async (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return;

    const newVipStatus = !customer.isVip;
    const newTags = newVipStatus 
      ? [...customer.tags, 'VIP'].filter((tag, index, self) => self.indexOf(tag) === index)
      : customer.tags.filter(tag => tag !== 'VIP');

    setCustomers(customers.map(c => 
      c.id === customerId 
        ? { ...c, isVip: newVipStatus, tags: newTags }
        : c
    ));
  };

  const sendCampaign = async () => {
    try {
      // Send to selected recipients
      await emailService.sendBatchEmails(
        campaignForm.recipients,
        campaignForm.subject,
        campaignForm.content
      );

      // Update campaign status
      const newCampaign: EmailCampaign = {
        id: Date.now().toString(),
        ...campaignForm,
        status: 'sent',
        sent_at: new Date().toISOString(),
        open_rate: 0,
        click_rate: 0,
      };

      setCampaigns([...campaigns, newCampaign]);
      setCampaignDialogOpen(false);
      setCampaignForm({ name: '', subject: '', content: '', recipients: [] });
    } catch (error) {
      console.error('Error sending campaign:', error);
    }
  };

  const getCustomerSegments = () => {
    const segments = [
      {
        name: 'All Customers',
        count: customers.length,
        icon: <Groups />,
        color: '#00d4ff',
      },
      {
        name: 'VIP Customers',
        count: customers.filter(c => c.isVip).length,
        icon: <Star />,
        color: '#ff0080',
      },
      {
        name: 'Frequent Buyers',
        count: customers.filter(c => c.tags.includes('Frequent Buyer')).length,
        icon: <TrendingUp />,
        color: '#00ff88',
      },
      {
        name: 'New Customers',
        count: customers.filter(c => {
          const daysSinceCreated = (Date.now() - new Date(c.created_at).getTime()) / (1000 * 60 * 60 * 24);
          return daysSinceCreated <= 30;
        }).length,
        icon: <PersonAdd />,
        color: '#ffaa00',
      },
    ];
    return segments;
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = !searchQuery || 
      customer.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.profile?.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.profile?.company_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesTag = !filterTag || customer.tags.includes(filterTag);
    
    return matchesSearch && matchesTag;
  });

  const paginatedCustomers = filteredCustomers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      {/* Header */}
      <Box sx={{
        mb: 4,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2, md: 0 },
        justifyContent: { xs: 'flex-start', md: 'space-between' },
        alignItems: { xs: 'stretch', md: 'center' }
      }}>
        <Box>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 800,
              background: 'linear-gradient(135deg, #ff0080 0%, #00d4ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('admin.crmAndMarketing')}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            {t('admin.manageCustomersAndMarketingCampaigns')}
          </Typography>
        </Box>
        <Box sx={{
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Button
            variant="outlined"
            startIcon={<PersonAdd />}
            fullWidth
            sx={{
              borderColor: 'rgba(0,212,255,0.5)',
              color: '#00d4ff',
              '&:hover': {
                borderColor: '#00d4ff',
                bgcolor: 'rgba(0,212,255,0.1)',
              },
            }}
          >
            {t('admin.addCustomer')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Campaign />}
            onClick={() => setCampaignDialogOpen(true)}
            fullWidth
            sx={{
              background: 'linear-gradient(135deg, #ff0080 0%, #cc0066 100%)',
              boxShadow: '0 2px 8px rgba(255,0,128,0.2)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 3px 12px rgba(255,0,128,0.25)',
              },
            }}
          >
            {t('admin.newCampaign')}
          </Button>
        </Box>
      </Box>

      <CustomerSegments segments={getCustomerSegments()} onSegmentClick={setFilterTag} />

      {/* Main Content */}
      <Paper
        sx={{
          bgcolor: 'background.paper',
          borderRadius: '8px',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            '& .MuiTab-root': {
              fontWeight: 600,
              minWidth: { xs: 'auto', sm: 120 },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              '&.Mui-selected': {
                color: '#00d4ff',
              },
            },
            '& .MuiTabs-indicator': {
              bgcolor: '#00d4ff',
              height: 3,
            },
          }}
        >
          <Tab label={`Customers (${customers.length})`} />
          <Tab label={`Campaigns (${campaigns.length})`} />
          <Tab label="Analytics" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <CustomersTab
              customers={customers}
              loading={loading}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filterTag={filterTag}
              setFilterTag={setFilterTag}
              viewMode={viewMode}
              setViewMode={setViewMode}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              onToggleVip={toggleCustomerVip}
              onEditCustomer={(customer) => {
                setSelectedCustomer(customer);
                setCustomerDialogOpen(true);
              }}
            />
          )}

          {activeTab === 1 && <CampaignsTab campaigns={campaigns} />}

          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('admin.customerAnalytics')}
              </Typography>
              <Alert severity="info" sx={{ mb: 3, borderRadius: '8px' }}>
                {t('admin.detailedAnalyticsComingSoon')}
              </Alert>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Card sx={{
                  bgcolor: 'background.paper',
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: 'divider',
                }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('admin.customerLifetimeValue')}
                    </Typography>
                    <Typography variant="h3" fontWeight={700} color="primary">
                      €{(customers.reduce((sum, c) => sum + c.totalSpent, 0) / customers.length || 0).toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('admin.averagePerCustomer')}
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{
                  bgcolor: 'background.paper',
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: 'divider',
                }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t('admin.repeatPurchaseRate')}
                    </Typography>
                    <Typography variant="h3" fontWeight={700} color="secondary.main">
                      {((customers.filter(c => c.orders.length > 1).length / customers.length) * 100 || 0).toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('admin.customersWithMultipleOrders')}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      <CampaignDialog
        open={campaignDialogOpen}
        onClose={() => setCampaignDialogOpen(false)}
        form={campaignForm}
        setForm={setCampaignForm}
        customers={customers}
        onSend={sendCampaign}
      />
    </Container>
  );
};

export default CRM;